/**
 * Las exclusiones del paso Audiencia (28/10/2026), puras: lo elegido vacío, si dice algo, qué listas elegidas ya no
 * están (se borraron), lo que se manda al contar y al guardar, y los textos. El bloque es `components/ExcluirBloque.tsx`
 * y la pantalla de las listas, `pages/ListasExclusionPage.tsx`. Las reglas (quién recibió, cómo se cruzan los
 * celulares) son del backend (`util/exclusiones.util.ts`).
 *
 * Desde el 29/10/2026 una lista puede valer para todas las campañas (`siempre`, prendida por defecto): ésas se aplican
 * solas en toda campaña de la marca y el bloque las muestra arriba; las demás se eligen por campaña. Una lista de un
 * backend anterior, sin el dato, vale siempre (como la lee el backend).
 */

import type { AudienciaResumen, CampanaParaExcluir, ExcluirAudiencia, ListaExclusion, ResumenExclusiones } from "../types";
import { normalizarTexto } from "./base";
import { num, plural } from "../../campana-nueva/utils/format";

/** La pantalla de las listas de exclusión (una página, no una ventana). */
export const RUTA_LISTAS_EXCLUSION = "/activa/oficial/exclusiones";

export const EXCLUIR_VACIO: ExcluirAudiencia = { campanas: [], listas: [] };

/** Desde cuántas campañas el bloque ofrece buscar. */
export const CAMPANAS_PARA_BUSCAR = 6;

/** ¿Vale para todas las campañas? Sin el dato (un backend anterior), sí: como la lee el backend. */
export const valeSiempre = (l: Pick<ListaExclusion, "siempre"> | null | undefined): boolean => l?.siempre !== false;

/** Las listas que valen para todas las campañas: se aplican sin elegirlas. */
export function listasSiempre(listas: ListaExclusion[] | null | undefined): ListaExclusion[] {
  return (listas || []).filter(valeSiempre);
}

/** Las listas que se eligen por campaña (con «Vale para todas las campañas» apagada). */
export function listasParaElegir(listas: ListaExclusion[] | null | undefined): ListaExclusion[] {
  return (listas || []).filter((l) => !valeSiempre(l));
}

/** ««Empleados»» / ««Empleados» y «VIP»» / ««A», «B» y «C»». */
export function unirConY(xs: string[]): string {
  const q = xs.map((x) => `«${x}»`);
  return q.length <= 1 ? q[0] ?? "" : `${q.slice(0, -1).join(", ")} y ${q[q.length - 1]}`;
}

/** «Siempre se saca a quien está en «Empleados» y «VIP».» (vacío sin listas que valgan siempre). */
export function textoSiempre(nombres: string[]): string {
  return nombres.length ? `Siempre se saca a quien está en ${unirConY(nombres)}.` : "";
}

/** ¿Elige algo? Sin campañas ni listas, es como no tenerlas (no se manda). */
export function excluirActivo(e: ExcluirAudiencia | null | undefined): e is ExcluirAudiencia {
  return !!e && (e.campanas.length > 0 || e.listas.length > 0);
}

/** Lo que se manda al guardar: lo elegido si dice algo, null si no (la audiencia se guarda sin la clave). */
export function excluirParaMandar(e: ExcluirAudiencia | null | undefined): ExcluirAudiencia | null {
  return excluirActivo(e) ? { campanas: [...e.campanas], listas: [...e.listas] } : null;
}

/** Lo guardado en una campaña, como ids (lo que el bloque marca). */
export function excluirDeAudiencia(a: AudienciaResumen["excluir"] | null | undefined): ExcluirAudiencia {
  return { campanas: (a?.campanas || []).map((c) => c.id), listas: (a?.listas || []).map((l) => l.id) };
}

/**
 * Las listas elegidas que ya no se pueden aplicar: las que la campaña guardó como borradas y, con las listas de la marca
 * ya cargadas, las que no están entre ellas (se borraron después). Con su nombre si se sabe.
 */
export function listasBorradas(
  e: ExcluirAudiencia,
  guardadas: AudienciaResumen["excluir"] | null | undefined,
  listas: ListaExclusion[] | null,
): { id: string; nombre: string }[] {
  const nombreGuardado = new Map((guardadas?.listas || []).map((l) => [l.id, l]));
  const vivas = listas ? new Set(listas.map((l) => l.id)) : null;
  return e.listas
    .filter((id) => nombreGuardado.get(id)?.borrada === true || (vivas !== null && !vivas.has(id)))
    .map((id) => ({ id, nombre: nombreGuardado.get(id)?.nombre || "" }));
}

/** Lo que se manda al contar: lo elegido sin las listas borradas (el backend no cuenta con una borrada: la dice). */
export function excluirParaContar(e: ExcluirAudiencia, borradas: { id: string }[]): ExcluirAudiencia | null {
  const fuera = new Set(borradas.map((b) => b.id));
  return excluirParaMandar({ campanas: e.campanas, listas: e.listas.filter((id) => !fuera.has(id)) });
}

/** Las campañas que coinciden con lo que se busca (sin tildes ni mayúsculas); sin búsqueda, todas. */
export function buscarCampanas(campanas: CampanaParaExcluir[], texto: string): CampanaParaExcluir[] {
  const t = normalizarTexto(texto || "");
  return t ? campanas.filter((c) => normalizarTexto(c.nombre).includes(t)) : campanas;
}

/** «20/10»: el día en que salió, en la hora de Argentina (vacío sin fecha). */
export function diaDeCampana(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "2-digit", timeZone: "America/Argentina/Buenos_Aires" }).format(d);
}

/** «saca 120» / «saca 1» / «no saca a nadie de esta audiencia»: a cuántos alcanza una cosa sola. */
export function textoSaca(n: number): string {
  return n > 0 ? `saca ${num(n)}` : "no saca a nadie de esta audiencia";
}

/** «Con las exclusiones quedan 880 de 1.000 personas (salen 120).» */
export function textoQuedanExclusiones(r: ResumenExclusiones): string {
  const quedan = Math.max(0, r.personas - r.fuera);
  return `Con las exclusiones ${plural(quedan, "queda", "quedan")} ${num(quedan)} de ${num(r.personas)} ${plural(r.personas, "persona", "personas")} (${plural(r.fuera, "sale", "salen")} ${num(r.fuera)}).`;
}

/** ««Promo octubre»», ««Promo» o «Día del padre»», ««A», «B» o «C»». */
function unir(xs: string[]): string {
  const q = xs.map((x) => `«${x}»`);
  return q.length <= 1 ? q[0] ?? "" : `${q.slice(0, -1).join(", ")} o ${q[q.length - 1]}`;
}

/**
 * Lo elegido en palabras, para la cabecera plegada: «Sin los que recibieron «Promo octubre»», «Sin la lista
 * «Empleados»». `nombres`: el nombre de cada id (de las opciones o de lo guardado).
 */
export function describirExclusiones(e: ExcluirAudiencia, nombres: Map<string, string>): string[] {
  const out: string[] = [];
  const de = (ids: string[]) => ids.map((id) => nombres.get(id) || "una campaña que ya no está");
  if (e.campanas.length) out.push(`Sin los que recibieron ${unir(de(e.campanas))}`);
  if (e.listas.length) out.push(`Sin ${e.listas.length === 1 ? "la lista" : "las listas"} ${unir(e.listas.map((id) => nombres.get(id) || "borrada"))}`);
  return out;
}

/**
 * Lo pegado en la pantalla de las listas, contado por renglón (lo que se lee de verdad lo decide el backend): cuántos
 * renglones con algo, para decir «12 renglones» mientras se escribe.
 */
export function renglonesConAlgo(texto: string): number {
  return String(texto || "").split(/[\r\n,;\t]+/).filter((x) => x.trim()).length;
}

/** El contenido de una lista en el cuadro de texto: un celular o un mail por renglón. */
export function textoDeEntradas(e: { celulares: string[]; mails: string[] }): string {
  return [...(e.celulares || []), ...(e.mails || [])].join("\n");
}

/** «12 celulares y 3 mails» / «1 celular» / «2 mails». */
export function textoContenido(l: { celulares: number; mails: number }): string {
  const partes = [
    l.celulares ? `${num(l.celulares)} ${plural(l.celulares, "celular", "celulares")}` : "",
    l.mails ? `${num(l.mails)} ${plural(l.mails, "mail", "mails")}` : "",
  ].filter(Boolean);
  return partes.length ? partes.join(" y ") : "vacía";
}
